import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { useUsersAutocomplete } from '@/hooks/autocomplete/useUsersAutocomplete';
import type { components } from '@/shared/api/openapi';

type UserAutocomplete = components['schemas']['GetUsersAutocompleteEndpoint_GetUsersResponse'];

export type GetUsersAutocompleteResponse = UserAutocomplete;

const parseEmailsParam = (searchParams: URLSearchParams): UserAutocomplete[] => {
  const emailsParam = searchParams.get('emails');
  if (!emailsParam) return [];
  return emailsParam
    .split(',')
    .filter(Boolean)
    .map((email) => ({ email }));
};

export interface UsersListAutocompleteProps {
  organizationId: number;
  disabled?: boolean;
}

function UsersListAutocomplete({ organizationId, disabled }: UsersListAutocompleteProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  const [selectedEmail, setSelectedEmail] = useState<UserAutocomplete[]>(() => parseEmailsParam(searchParams));

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading } = useUsersAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: !disabled,
  });

  const handleInputChange = (_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  };

  const handleEmailChange = (_: React.SyntheticEvent, emails: UserAutocomplete[]) => {
    setSelectedEmail(emails);
    if (emails.length === 0) {
      const params = new URLSearchParams(searchParams);
      params.delete('emails');
      params.delete('page');
      setSearchParams(params, { replace: true });
    }
  };

  const handleFilterApply = (emails: UserAutocomplete[]) => {
    const params = new URLSearchParams(searchParams);
    const extractedEmails = emails.map((e) => e.email).filter((s): s is string => !!s);

    if (extractedEmails.length > 0) {
      params.set('emails', extractedEmails.join(','));
    } else {
      params.delete('emails');
    }
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const getOptionLabel = (option: UserAutocomplete) => {
    return option.email || '';
  };

  const getOptionDisabled = (optionLabel: UserAutocomplete) => {
    return selectedEmail.some((val) => val.email === optionLabel.email);
  };

  const getOptionKey = (option: UserAutocomplete) => option.id ?? `key-${option.email}`;

  return (
    <BaseMultiAutocomplete<UserAutocomplete>
      autocomplete={{
        options: data || [],
        value: selectedEmail,
        loading: isLoading,
        onChange: handleEmailChange,
        onInputChange: handleInputChange,
        getOptionLabel: getOptionLabel,
        getOptionKey: getOptionKey,
        getOptionDisabled: getOptionDisabled,
        label: 'Filter by User',
        placeholder: 'Enter at least 3 characters to search',
        disabled: disabled ?? false,
        filterOptions: (x) => x,
      }}
      actionButton={{
        buttonLabel: 'Filter',
        buttonRef: buttonRef,
        onApply: handleFilterApply,
      }}
    />
  );
}

export default UsersListAutocomplete;
